<template>
	<!--Absolute quick scroll buttons-->
	<div class="quick-scroll" v-if="!loading && !newProperty">
		<a @click="scrollToId('#propertyData')" ><span class="hidden xl:block">Property Details</span><span class="xl:hidden">Property</span></a>
		<a @click="scrollToId('#propertyServiceList')" >Services</a>
		<a @click="scrollToId('#documentList')" >Documents</a>
		<a @click="scrollToId('#ownerDetails')" >Owner</a>
		<a @click="scrollToId('#bwofEvac')" >Bwof</a>
	</div>

	<!-- Title -->
	<div class="flex flex-wrap align-items-center scroll-mt-70 sm:mb-2 md:mb-0" style="height:64px;margin-bottom:9rem" id="propertyData">
		<div class="col">
			<h3 class="mb-0">Property: {{receivedData.propertyName}}</h3>
		</div>
		<div class="col-fixed" v-if="!loading && isInternalUser">
			<label>Active<br /></label>
			<InputSwitch v-model="receivedData.active" v-tooltip.top="'Active Property'" />
		</div>
		<div class="col-fixed text-right" v-if="!this.newProperty">
			<h3 class="mb-0"># {{ propertyId }}</h3>
		</div>
	</div>

	<!-- Property Data-->
	<div class="flex flex-row flex-wrap">
		<div class="col-12 xxl:col-6">
			<div class="card bg-compass-red">
				<div class="fluid grid">

					<div class="field col-12 md:col-6">
						<a @click="enableNameEdit" v-if="isInternalUser">
							<i class="pi pi-lock" v-if="nameLocked"></i>
							<i class="pi pi-lock-open" v-if="!nameLocked"></i>
						</a>
						<label>Property Name *</label>
						<InputText v-model="receivedData.propertyName" :disabled="nameLocked" />
					</div>

					<div class="field col-12 md:col-6">
						<label>Building Name</label>
						<InputText v-model="receivedData.buildingName" />
					</div>

					<div class="field col-12 md:col-8">
						<label>Street Address *</label>
						<InputText v-model="receivedData.streetAddress" />
					</div>

					<div class="field col-12 md:col-4">
						<label>Customer Property Ref #</label>
						<InputText v-model="receivedData.customerPropertyRef" />
					</div>

					<div class="field col-12 md:col-5">
						<label>Suburb</label>
						<InputText v-model="receivedData.suburb" />
					</div>

					<div class="field col-12 md:col-5">
						<label>City/Town</label>
						<InputText v-model="receivedData.city" />
					</div>

					<div class="field col-12 md:col-2">
						<label>Post Code</label>
						<InputText v-model="receivedData.postcode" />
					</div>

					<template v-if="isInternalUser">
						<div class="field col-12 xl:col-8">
							<div class="mb-2" style="white-space:nowrap">
								<label>Regional Authority</label>

								<div style="display:inline-block" v-if="receivedData.regionalAuthorityCompanyId != null && isInternalUser">
									<a class="ml-2" @click.exact="navigation.toCompanyDetailsScreen(receivedData.regionalAuthorityCompanyId)" @click.shift="navigation.toCompanyDetailsScreen(receivedData.regionalAuthorityCompanyId,true)">
										<i class="pi pi-external-link" style="font-size: 0.75rem;color:white" />
									</a>

									<a class="ml-3" @click="toggleAuthorityEmail()" v-tooltip.top="'Email the Regional Authority'" v-if="$store.getters.isInternalUser">
										<i class="pi pi-envelope" style="font-size: 0.75rem; color: white;" />
									</a>
								</div>

								<i class="pi pi-question-circle float-right" style="font-size: 1rem; color: white;" v-tooltip.top="'Regional Authority (Council), which also determines North Island vs South Island location of this property'" />
							</div>

							<Multiselect :options="authorityList"
										 :searchable="true"
										 :limit="40"
										 :filter="true"
										 class="w-100pc"
										 v-model="receivedData.regionalAuthorityCompanyId"
										 valueProp="id"
										 label="nameAndId"
										 track-by="nameAndId"
										 placeholder="Select Regional Authority" />
						</div>

						<div class="field col-12 xl:col-4">
							<label>Bwof Template <i class="pi pi-question-circle" style="font-size: 1rem; color: white;" v-tooltip.top="'Overrides which customer logo shows on this property BWOF. Leave empty for the Compass logo.'" /></label><br />
							<Multiselect :options="logoOptions"
										 :searchable="true"
										 :filter="true"
										 v-model="receivedData.bwofLogoId"
										 valueProp="customerLogoId"
										 label="logoName"
										 track-by="logoName"
										 placeholder="(Empty for Compass)" />
						</div>
					</template>

					<div class="col-12">
						<span class="float-right">Created: {{formattedDate(receivedData.whenCreated)}}</span>
					</div>
				</div>
			</div>
		</div>
		<!--Property Notes--> 
		<div class="col-12 xxl:col-6">
			<template v-if="isInternalUser">
				<div class="card">
					<div class="fluid grid">
						<div class="field col-12">
							<label>Internal Property Notes</label>
							<TextArea v-model="receivedData.propertyNotes" rows="10" :autoResize="true" />
						</div>
					</div>
				</div>
				<div class="flex">
					<div class="col-fixed" v-if="!loading">
						<label>Enable Automation Emails<br /></label>
						<InputSwitch v-model="receivedData.enableEmailAutomation" />
					</div>
				</div>
			</template>
		</div>
	</div>

	<!-- Customer and Billing -->
	<PropertyCustomerDetails :customerCompanyId="receivedData.customerCompanyId"
							 @updateCustomerCompanyId="receivedData.customerCompanyId = $event;"
							 :billingEntityCompanyId="receivedData.billingEntityCompanyId"
							 @updateBillingEntityCompanyId="receivedData.billingEntityCompanyId = $event;"
							 :receivedBillingEntity="receivedData.billingEntity"
							 @updateBillingEntity="receivedData.billingEntity = $event;"
							 @updateBillingEntityId="receivedData.billingEntityId = $event;"
							 v-if="!loading && !newProperty && isInternalUser" />

	<!-- Site Contacts-->
	<div class="col-12" v-if="!loading && !newProperty">
		<div class="card">
			<Contacts title="Site Contacts" :contacts="receivedData.siteContacts" view="toggle" :canEdit="isInternalUser" />
		</div>
	</div>

	<!-- Services Table-->
	<div id="propertyServiceList" class="scroll-mt-70">
		<PropertyServiceTable :services="receivedData.services" 
							  :propertyData="receivedData" 
							  v-if="!loading && !newProperty" />
	</div>

	<!-- Document List -->
	<div id="documentList" class="col-12 scroll-mt-70" v-if="!loading && !newProperty">
		<DocumentList :propertyData="receivedData" v-if="!loading" :canEdit="isInternalUser"></DocumentList>
	</div>

	<!-- Owner Details -->
	<div id="ownerDetails" class="col-12 scroll-mt-70" v-if="!loading && !newProperty">
		<div class="card">
			<div class="grid">
				<!-- Title -->
				<div class="col">
					<!-- External link to Owner page-->
					<h3>
						Owner
						<a @click.exact="navigation.toCompanyDetailsScreen(receivedData.owner.companyId)" @click.shift="navigation.toCompanyDetailsScreen(receivedData.owner.companyId)" v-if="!editOwnerMode && isInternalUser && receivedData.owner != undefined">
							<i class="pi pi-external-link p-1" style="font-size: 1rem;" />
						</a>
					</h3>
				</div>

				<div class="col-fixed align-self-end align-items-center" v-if="isInternalUser">
					<a @click="editOwnerMode=!editOwnerMode;">
						<i v-if="editOwnerMode" class="pi pi-lock-open" style="font-size: 1.5rem" />
						<i v-if="!editOwnerMode" class="pi pi-lock" style="font-size: 1.5rem" />
					</a>
				</div>
			</div>

			<div class="fluid grid" v-if="receivedData.owner && !editOwnerMode">
				<div class="field col-12 md:col-4">
					<label>Name</label>
					<InputText v-model="receivedData.owner.companyName" :disabled="true" />
				</div>
				<div class="field col-12 md:col-4">
					<label>Phone</label>
					<InputText v-model="receivedData.owner.companyPhone" :disabled="true" />
				</div>
				<div class="hidden field col-12 md:col-4">
					<label>Account No</label>
					<InputText v-model="receivedData.owner.accountNumber" :disabled="true" />
				</div>
				<div class="field col-12 md:col-4">
					<label>City/Town</label>
					<InputText v-model="receivedData.owner.city" :disabled="true" />
				</div>
			</div>
			<div class="fluid grid" v-else>
				<div class="field col-12 md:col-4" v-if="editOwnerMode">
					<Dropdown class="flex" v-model="selectedOwner" :options="ownerList" placeholder="Select Owner" :filter="true" :filterFields="['name','id']" filterPlaceholder="Find Owner" @change="confirmOwner">
						<template #value="data">
							<span v-if="data.value.name">{{data.value.name}} ({{data.value.id}})</span>
						</template>
						<template #option="data">
							<span>{{data.option.name}} ({{data.option.id}})</span>
						</template>
					</Dropdown>
				</div>
			</div>
			<div v-if="!editOwnerMode">
				<Popper class="popper-wrapper col-2" placement="right" :show="isOwnerContactSectionTooltipShown">
					<span class="compass-red mb-3" style="font-size: 1rem; font-weight: 500; line-height: 1.2;">Owner Contacts</span>
					
					<template #content>
						<div class="popper-tooltip">Note: No owner contact is selected to be displayed in BWOF (BWOF owner details pane)</div>
					</template>
				</Popper>
					
				<Contacts :contacts="receivedData.ownerContacts" view="indented-list" />
			</div>
		</div>
	</div>

	<!-- Property BWOF / Property Evacuation cards -->
	<div class="flex flex-row flex-wrap scroll-mt-70" v-if="!loading && !newProperty" id="bwofEvac">
		<!--Only BWOF visible to external users-->
		<div class="col-12 xxl:col-6">
			<PropertyBwof :receivedBwofData="receivedData.propertyBwof"
						  @updateBwofData="receivedData.propertyBwof = $event;"
						  :ownerContacts="receivedData.ownerContacts"
						  v-if="!loading" />
		</div>
		<div class="col-12 xxl:col-6" v-if="isInternalUser">
			<PropertyEvacuation :receivedEvacuationData="receivedData.propertyEvacuation"
								@updateEvacData="receivedData.propertyEvacuation = $event;"
								v-if="!loading" />
		</div>
	</div>

	<!--Document generation component for Regional Authority Email-->
	<DocumentGeneration :selectedDocumentTemplate="selectedDocumentTemplate"
						:propertyData="receivedData"
						:recipientCompanyId="receivedData.regionalAuthorityCompanyId"
						:showDocumentPanel="false"
						@updateShowDocumentGenerationComponent="showDocumentGenerationComponent = $event;"
						v-if="showDocumentGenerationComponent" />

	<!-- Floating Save/Cancel buttons -->
	<Button class="sticky-button cancel btn-fixed-width" icon="pi pi-times" :label="hasChanges ? 'Cancel' : 'Close'" @click="cancel" v-if="!loading" />
	<Button class="sticky-button btn-fixed-width" icon="pi pi-check" label="Save" @click="save" v-if="!loading && !newProperty && isInternalUser" :disabled="!hasChanges" />
	<Button class="sticky-button" icon="pi pi-check" label="Add new record" @click="saveNewProperty" v-if="!loading && newProperty && isInternalUser" />
</template>

<script lang="ts">
	import { CompassApi } from '@/api-client/CompassApiProxy';
	import { allowLeaveOnlyWithConfirmationOrNoChanges } from '@/components/LeaveWithoutSavingPrompt';
	import { CompanyListFlags } from '@/utils/CompanyListFlags';
	import { Navigation } from '@/utils/Navigation';
	import { email, required } from "@vuelidate/validators";
	import { format, isValid } from 'date-fns';
	import { Options, Vue } from 'vue-class-component';
	import { CompanyListData, ContactData, CustomerLogoOption, EmailTemplateType, PropertyData } from '../api-client/api-client';
	import { GetBwofServiceID } from '../utils/CompassConstants';
	import Toast from '@/utils/Toast';
	import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';

	Vue.registerHooks([
		'beforeRouteLeave',
	])

	@Options({
		props: {
			propertyId: 0
		}
	})
	export default class PropertyDetails extends Vue {

		propertyId!: number;  // Actually passed as a prop

		receivedData: PropertyData = new PropertyData;

		/** Used to record the data as received on first load, so we can detect any changes*/
		receivedDataJSON = '';

		propertyListFilter = "";

		serviceListFilter = "";

		nameLocked = true;

		ownerList: CompanyListData[] = [];

		selectedOwner: CompanyListData = new CompanyListData();

		editOwnerMode = false;

		loading = false;

		newProperty = false;

		isInternalUser = false;

		navigation = Navigation;

		showDocumentGenerationComponent = false;
		selectedDocumentTemplate: string | undefined = undefined;

		logoOptions: CustomerLogoOption[] = [];

		created() {
			this.loading = true;

			// Set intenalUser flag based on token
			this.isInternalUser = this.$store.getters.isInternalUser;

			// We will need company list for the set owner dropdown, ensure it is loaded
			if(this.isInternalUser) {
				this.$store.dispatch('downloadCompanyList');
				this.loadLogoOptions();
			}

			// Set behaviour if we are creating a new property
			if (this.propertyId < 0) {
				this.newProperty = true;
				this.nameLocked = false;
				this.receivedData.active = true;
				this.receivedData.siteContacts = [];
				this.receivedData.ownerContacts = [];
				this.receivedData.enableEmailAutomation = true;
				this.loading = false;
				return;
			}

			this.loadData();
		}

		// Retrieve data from API
		loadData() {
			const apiClient = CompassApi.propertyApi(this.$store);
			apiClient.getPropertyById(this.propertyId)
				.then(result => {
					this.receivedData = result;
					this.receivedDataJSON = JSON.stringify(result, null, 1);
					this.ownerList = this.$store.state.companyList.data;

					if (result && result.owner) {
						const foundOwner = this.ownerList.find(owner => owner.id == result.owner?.companyId)
						this.selectedOwner = foundOwner ? foundOwner : this.selectedOwner;
					}

					this.loading = false;
				})
				.catch(error => {
					Toast.ApiError('Error loading property ' + this.propertyId, error);
					this.loading = false;
				});
		}

		  beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
			allowLeaveOnlyWithConfirmationOrNoChanges(this, this.hasChanges, next);
		}

		enableNameEdit() {
			this.nameLocked = !this.nameLocked;
		}

		validations() {
			return {
				name: {
					required
				},
				email: {
					required,
					email
				},
				password: {
					required
				},
				accept: {
					required
				}
			}
		}

		// Actions
		save() {
			// Remove last contact if it's empty
			this.removeEmptySiteContact();

			if (this.emptyBwof) this.receivedData.propertyBwof = undefined;
			if (this.emptyEvac) this.receivedData.propertyEvacuation = undefined;

			if (this.propertyId < 0) {
				console.error(`Property identifier is less than 0. This should not be possible.`)
				return;
			}

			// save property  data
			const apiClient = CompassApi.propertyApi(this.$store);
			apiClient.updateProperty(this.propertyId, this.receivedData)
				.then(result => {
					this.$store.dispatch('downloadPropertyList');

					// Replace old data record with a freshly received one
					this.receivedData = result;

					// Remember the data as saved, so we only enable save button etc if there are changes after this
					this.receivedDataJSON = JSON.stringify(result, null, 1);

					Toast.RemoveAllGroups();
					Toast.RemoveAllGroups();
					Toast.Success('Save', 'Property details have been saved.');
				})
				.catch(error => {
					Toast.ApiError('Error saving property ' + this.propertyId, error);
				});
		}

		cancel() {
			this.$router.go(-1);
		}

		saveNewProperty() {
			// create property record
			const apiClient = CompassApi.propertyApi(this.$store);
			apiClient.addProperty(this.receivedData)
				.then(result => {
					this.$store.dispatch('downloadPropertyList');

					// result has the new propertyid
					const newUrl = window.location.href.replace("/new", "/" + JSON.stringify(result));
					window.location.replace(newUrl);
				})
				.catch(error => {
					Toast.ApiError('Error creating property ' + this.receivedData.propertyName, error);
				});
		}

		//** Confirm details of the selected property owner */
		confirmOwner() {
			if (!this.selectedOwner?.id) return;

			const apiClient = CompassApi.companyApi(this.$store);
			apiClient.getCompanyById(this.selectedOwner.id)
				.then(result => {

					// Remove the ownerContactId and ownerContact in the BWOF before changing owner data
					if (this.receivedData.propertyBwof) {
						this.receivedData.propertyBwof.ownerContactId = undefined;
						this.receivedData.propertyBwof.ownerContact = undefined;
					}

					// Set owner details for property record
					this.receivedData.ownerId = result.companyId;
					this.receivedData.owner = result;
					this.receivedData.ownerContacts = result.contacts;

					// Set owner contact to default available contact when owner details change.
					if (this.receivedData.propertyBwof) {
						
						const firstOwnerContact = result.contacts && result.contacts?.length > 0 ? result.contacts[0] : null;
						if (firstOwnerContact) {
							this.receivedData.propertyBwof.ownerContactId = firstOwnerContact.contactId;
							this.receivedData.propertyBwof.ownerContact = firstOwnerContact;
						}
					}

					this.editOwnerMode = false;
				})
				.catch(error => {
					Toast.ApiError('Error retrieving owner ' + this.selectedOwner.id, error);
				});
		}

		removeEmptySiteContact() {
			if (this.receivedData.siteContacts != null) {
				const lastContact: ContactData = this.receivedData.siteContacts[this.receivedData.siteContacts.length - 1];
				if (lastContact != null && lastContact.firstName == null && lastContact.lastName == null && lastContact.email == null) {
					this.receivedData.siteContacts?.splice(this.receivedData.siteContacts.length - 1, 1);
				}
			}
		}

		scrollToId(id: string) {
			const element = document.querySelector(id);
			if (element) {
				element.scrollIntoView({
					behavior: 'smooth',
					block: 'start' // start, center, end, nearest
				})
			}
		}

		formattedDate(date: Date) {
			if (!isValid(date)) return;

			return format(date, 'dd MMM yyyy');
		}

		toggleAuthorityEmail() {
			this.selectedDocumentTemplate = EmailTemplateType[EmailTemplateType.Email_AuthorityOverdueBwofNotification].toString();
			this.showDocumentGenerationComponent = true;
		}

		get hasChanges(): boolean {
			// External users can't save, so we always return "no changes"
			if (!this.isInternalUser)
				return false;

			const currentJSON = JSON.stringify(this.receivedData, null, 1);
			return (currentJSON != this.receivedDataJSON);
		}

		get authorityList() {
			return this.$store.state.companyList.data.filter(c => CompanyListFlags.isRegionalAuthority(c));
		}

		loadLogoOptions(): void {
			const apiClient = CompassApi.companyApi(this.$store);

			apiClient.getCustomerLogoOptions()
				.then(result => {
					this.logoOptions = result;
				});
		}

		get emptyBwof() {
			if (!this.receivedData.propertyBwof) return true;
			return (this.receivedData.propertyBwof.bwofIssuedBy == null
				&& this.receivedData.propertyBwof.bwofIssuedDate == null
				&& this.receivedData.propertyBwof.intendedLifeOfTheBuilding == null
				&& this.receivedData.propertyBwof.firstConstructed == null
				&& this.receivedData.propertyBwof.bwofLocated == null
				&& this.receivedData.propertyBwof.buildingSafetyManualLocated == null
				&& this.receivedData.propertyBwof.buildingSafetyManualIssuedOrUpdated == null
				&& this.receivedData.propertyBwof.buildingConsent == null
				&& this.receivedData.propertyBwof.complianceSchedule == null
				&& this.receivedData.propertyBwof.codeComplianceCertificate == null
				&& this.receivedData.propertyBwof.complianceScheduleMonths == null
				&& this.receivedData.propertyBwof.legalDescription == null
				&& this.receivedData.propertyBwof.locationOfBuildingWithinSite == null
				&& this.receivedData.propertyBwof.buildingUse == null
				&& this.receivedData.propertyBwof.purposeGroup == null
				&& this.receivedData.propertyBwof.maximumNumberOfOccupants == null
				&& this.receivedData.propertyBwof.highestFireHazardCategoryFhcNumber == null
				&& this.receivedData.propertyBwof.levelUnitNo == null
				&& this.receivedData.propertyBwof.issuedBy == null
				&& this.receivedData.propertyBwof.ownerInspectionsCompletedBy == null) ? true : false;
		}

		get emptyEvac() {
			if (!this.receivedData.propertyEvacuation) return true;
			return (this.receivedData.propertyEvacuation.fireEvacuationServiceType == null
				&& this.receivedData.propertyEvacuation.fireEvacuationManualLastUpdated == null
				&& this.receivedData.propertyEvacuation.wardenTraining == null
				&& this.receivedData.propertyEvacuation.nzfsApproval == null
				&& this.receivedData.propertyEvacuation.nzfApplicationDate == null
				&& this.receivedData.propertyEvacuation.securityAlarmType == null
				&& this.receivedData.propertyEvacuation.fireAlarmType == null
				&& this.receivedData.propertyEvacuation.fireAlarmPanelLocated == null
				&& this.receivedData.propertyEvacuation.schemeReference == null
				&& this.receivedData.propertyEvacuation.fireAlarmMonitoredBy == null) ? true : false;
		}

		get isOwnerContactSectionTooltipShown() {
			if (this.receivedData && this.receivedData.ownerContacts && this.receivedData.ownerContacts.length > 0 && this.receivedData.propertyBwof && this.receivedData.propertyBwof.ownerContact == null && this.hasBwofSubService)
				return true;

			return false;
		}

		get hasBwofSubService() {
			return this.receivedData?.services && this.receivedData?.services.filter(s => s.parentServiceId == GetBwofServiceID())?.length > 0;
		}
	}
</script>

<style scoped lang="scss">
	.btn-fixed-width {
		width: 100px;
	}
</style>